/**
 * Mix & Match Bundle
 *
 * Custom element powering the "Mix & Match Bundle" section.
 * Handles product selection limits, variant switching, validation
 * and adding the completed bundle to the cart via the Ajax Cart API.
 */
(() => {
  'use strict';

  class MixMatchBundle extends HTMLElement {
    connectedCallback() {
      // Guard against repeat connections (e.g. theme editor section moves).
      if (this.initialized) return;
      this.initialized = true;

      this.bundleLimit = parseInt(this.dataset.bundleLimit, 10) || 2;
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add';
      // When set, the whole bundle is sold as this single fixed-price variant.
      this.bundleVariantId = parseInt(this.dataset.bundleVariantId, 10) || null;
      this.bundleTitle = this.dataset.bundleTitle || 'Bundle';

      this.counterCurrentEl = this.querySelector('[data-counter-current]');
      this.counterEl = this.querySelector('[data-counter]');
      this.addButton = this.querySelector('[data-add-button]');
      this.spinner = this.querySelector('[data-spinner]');
      this.errorEl = this.querySelector('[data-error]');

      this.cards = Array.from(this.querySelectorAll('[data-product-card]'));
      this.variantData = new Map();
      this.cards.forEach((card) => this.parseVariantData(card));

      this.addEventListener('change', this.handleChange.bind(this));
      if (this.addButton) {
        this.addButton.addEventListener('click', this.handleAddToCart.bind(this));
      }

      this.updateSelectionState();
    }

    /* ------------------------------------------------------------------ */
    /* Setup                                                               */
    /* ------------------------------------------------------------------ */

    parseVariantData(card) {
      const script = card.querySelector('[data-variant-data]');
      if (!script) return;

      try {
        this.variantData.set(card.dataset.productId, JSON.parse(script.textContent));
      } catch (error) {
        console.error('[MixMatchBundle] Invalid variant JSON for product', card.dataset.productId, error);
        this.variantData.set(card.dataset.productId, []);
      }
    }

    /* ------------------------------------------------------------------ */
    /* Event handling                                                      */
    /* ------------------------------------------------------------------ */

    handleChange(event) {
      const card = event.target.closest('[data-product-card]');
      if (!card) return;

      if (event.target.matches('[data-select-checkbox]')) {
        this.updateSelectionState();
      } else if (event.target.matches('[data-option-select]')) {
        this.handleVariantChange(card);
      }
    }

    handleVariantChange(card) {
      const variants = this.variantData.get(card.dataset.productId) || [];
      const selects = Array.from(card.querySelectorAll('[data-option-select]'));
      const selectedOptions = selects.map((select) => select.value);

      const variant = variants.find((candidate) =>
        candidate.options.every((option, index) => option === selectedOptions[index])
      );

      this.updateCardVariant(card, variant);
      this.updateSelectionState();
    }

    updateCardVariant(card, variant) {
      const checkbox = card.querySelector('[data-select-checkbox]');
      const priceEl = card.querySelector('[data-price]');
      const comparePriceEl = card.querySelector('[data-compare-price]');
      const stockEl = card.querySelector('[data-stock-status]');
      const imageEl = card.querySelector('[data-product-image]');

      const available = Boolean(variant && variant.available);
      const isDefaultVariant = Boolean(variant && variant.title === 'Default Title');

      card.dataset.variantId = variant ? String(variant.id) : '';
      card.dataset.variantTitle = variant && !isDefaultVariant ? variant.title : '';
      card.dataset.available = String(available);
      card.classList.toggle('mmb-card--sold-out', !available);

      if (priceEl && variant) priceEl.textContent = variant.price;

      if (comparePriceEl) {
        const hasCompare = Boolean(variant && variant.compare_at_price);
        comparePriceEl.hidden = !hasCompare;
        if (hasCompare) comparePriceEl.textContent = variant.compare_at_price;
      }

      if (stockEl) {
        let label;
        if (!variant) {
          label = this.dataset.msgUnavailable;
        } else {
          label = available ? this.dataset.msgInStock : this.dataset.msgSoldOut;
        }
        stockEl.textContent = label;
        stockEl.classList.toggle('mmb-card__stock--sold-out', !available);
      }

      if (imageEl && variant && variant.featured_image) {
        imageEl.src = variant.featured_image;
        imageEl.removeAttribute('srcset');
      }

      if (checkbox && !available && checkbox.checked) {
        checkbox.checked = false;
      }
      if (checkbox) checkbox.disabled = !available;
    }

    /* ------------------------------------------------------------------ */
    /* Selection state                                                     */
    /* ------------------------------------------------------------------ */

    getSelectedCards() {
      return this.cards.filter((card) => {
        const checkbox = card.querySelector('[data-select-checkbox]');
        return checkbox && checkbox.checked;
      });
    }

    getSelectionLabel(card) {
      return card.dataset.productTitle;
    }

    getSelectionLabels(selectedCards) {
      return selectedCards.map((card) => this.getSelectionLabel(card));
    }

    updateSelectionState() {
      const selectedCards = this.getSelectedCards();
      const count = selectedCards.length;
      const limitReached = count >= this.bundleLimit;

      if (this.counterCurrentEl) this.counterCurrentEl.textContent = String(count);
      if (this.counterEl) this.counterEl.classList.toggle('mmb-counter--complete', limitReached);

      this.cards.forEach((card) => {
        const checkbox = card.querySelector('[data-select-checkbox]');
        if (!checkbox) return;

        const isSelected = checkbox.checked;
        const isAvailable = card.dataset.available === 'true';
        const isLocked = !isAvailable || (limitReached && !isSelected);

        checkbox.disabled = isLocked;
        card.classList.toggle('mmb-card--selected', isSelected);
        card.classList.toggle('mmb-card--disabled', limitReached && !isSelected && isAvailable);
      });

      if (this.addButton) this.addButton.disabled = count !== this.bundleLimit || this.loading;
      if (count === this.bundleLimit) this.hideError();
    }

    /* ------------------------------------------------------------------ */
    /* Cart                                                                */
    /* ------------------------------------------------------------------ */

    /**
     * Builds cart line items.
     *
     * - No fixed bundle product: each selected variant is added at its price.
     * - Fixed bundle product set: only that one paid line is added. Customer
     *   picks are stored as line item properties + order note (not as extra
     *   cart products), so the total stays the bundle price only.
     */
    buildCartItems(selectedCards) {
      const labels = this.getSelectionLabels(selectedCards);

      if (!this.bundleVariantId) {
        return selectedCards.map((card, index) => ({
          id: parseInt(card.dataset.variantId, 10),
          quantity: 1,
          properties: {
            'Bundle item': String(index + 1),
          },
        }));
      }

      const properties = {};
      labels.forEach((label, index) => {
        properties[`Item ${index + 1}`] = label;
      });

      return [
        {
          id: this.bundleVariantId,
          quantity: 1,
          properties,
        },
      ];
    }

    /**
     * Order note used by Trax as special_instructions (max 190 characters).
     * Full product names stay on the line item as Item 1 / Item 2 / Item 3.
     */
    buildOrderNote(labels) {
      const maxLength = 190;
      let note = labels.map((label, index) => `${index + 1}) ${label}`).join(' | ');

      if (note.length <= maxLength) return note;

      // Shorten each label evenly so all picks still appear within the limit.
      const separators = ' | '.length * Math.max(labels.length - 1, 0);
      const prefixes = labels.reduce((sum, _, index) => sum + String(index + 1).length + 2, 0);
      const available = Math.max(maxLength - separators - prefixes, labels.length * 8);
      const perLabel = Math.max(8, Math.floor(available / labels.length));

      note = labels
        .map((label, index) => {
          const short =
            label.length > perLabel ? `${label.slice(0, Math.max(perLabel - 1, 1)).trim()}…` : label;
          return `${index + 1}) ${short}`;
        })
        .join(' | ');

      if (note.length > maxLength) {
        note = `${note.slice(0, maxLength - 1).trim()}…`;
      }

      return note;
    }

    async updateCartNote(labels) {
      const cartBase = this.cartUrl.replace(/\/$/, '');
      const updateUrl = `${cartBase}/update.js`;
      const cartJsUrl = `${cartBase}.js`;
      const note = this.buildOrderNote(labels);

      // Always replace note and clear the old "Bundle selections" cart attribute
      // left over from earlier versions (it was showing on past orders).
      const payload = {
        note,
        attributes: {
          'Bundle selections': '',
        },
      };

      const writeNote = async () => {
        const response = await fetch(updateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error('Could not update cart note');
        }

        return response.json();
      };

      let cart = await writeNote();

      // If a previous note stuck around, force one more write.
      if (cart.note !== note) {
        cart = await writeNote();
      }

      // Final check against /cart.js in case the theme mutated cart state.
      const verified = await fetch(cartJsUrl, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      }).then((res) => res.json());

      if (verified.note !== note) {
        await writeNote();
      }
    }

    async handleAddToCart() {
      const selectedCards = this.getSelectedCards();

      if (selectedCards.length !== this.bundleLimit) {
        this.showError(this.dataset.msgIncomplete);
        return;
      }

      const labels = this.getSelectionLabels(selectedCards);
      const items = this.buildCartItems(selectedCards);

      this.setLoading(true);
      this.hideError();

      try {
        const response = await fetch(`${this.cartAddUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || this.dataset.msgCartError);
        }

        // Replace any previous cart note/attributes with this bundle's picks.
        await this.updateCartNote(labels);

        window.location.href = this.cartUrl;
      } catch (error) {
        this.setLoading(false);
        this.showError(error.message || this.dataset.msgCartError);
      }
    }

    /* ------------------------------------------------------------------ */
    /* UI helpers                                                          */
    /* ------------------------------------------------------------------ */

    setLoading(isLoading) {
      this.loading = isLoading;
      if (!this.addButton) return;

      this.addButton.disabled = isLoading;
      this.addButton.classList.toggle('mmb-add-button--loading', isLoading);
      this.addButton.setAttribute('aria-busy', String(isLoading));
      if (this.spinner) this.spinner.hidden = !isLoading;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }

  if (!customElements.get('mix-match-bundle')) {
    customElements.define('mix-match-bundle', MixMatchBundle);
  }
})();
